import React from "react";
import { motion } from "framer-motion";
import { Zap } from "lucide-react";
import { useTranslation } from "../contexts/LocaleContext";

interface SubscriptionPlanCardsProps {
  onSubscribe: (plan: "monthly" | "yearly" | "lifetime") => void;
  isLoading?: boolean;
}

export default function SubscriptionPlanCards({
  onSubscribe,
  isLoading = false,
}: SubscriptionPlanCardsProps) {
  const t = useTranslation();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 w-full max-w-2xl mx-auto items-stretch">
      {/* Monthly */}
      <motion.div
        whileHover={{ y: -3 }}
        className="relative p-5 md:p-6 rounded-[2rem] bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 flex flex-col items-center text-center group transition-colors hover:border-purple-200 dark:hover:border-purple-900 shadow-sm h-full"
      >
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 py-1 px-3 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[9px] font-black uppercase tracking-wider shadow-sm border border-slate-100 dark:border-slate-600 whitespace-nowrap">
          {t.landing.pricing.mostPopular}
        </div>
        <div className="flex-1 flex flex-col items-center">
          <span className="text-[10px] md:text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1 mt-1">
            {t.userSettings.trialExpired.monthly}
          </span>
          <div className="flex items-baseline gap-1 mb-3 md:mb-4">
            <span className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white">
              {t.landing.pricing.monthlyPrice}
            </span>
            <span className="text-slate-500 dark:text-slate-400 text-xs md:text-sm font-medium">
              {t.userSettings.trialExpired.periodMonth}
            </span>
          </div>
        </div>
        <button
          onClick={() => onSubscribe("monthly")}
          disabled={isLoading}
          className="w-full mt-auto flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-br from-purple-600/90 to-indigo-700/90 text-white font-black text-sm md:text-base shadow-lg hover:from-purple-600 hover:to-indigo-700 transition-all duration-300 transform active:scale-95 disabled:opacity-50"
        >
          <span>{t.userSettings.trialExpired.subscribe}</span>
          <Zap size={14} className="fill-current" />
        </button>
      </motion.div>

      {/* Yearly */}
      <motion.div
        whileHover={{ y: -3 }}
        className="relative p-5 md:p-6 rounded-[2rem] bg-gradient-to-br from-purple-600 to-indigo-700 text-white flex flex-col items-center text-center shadow-xl shadow-indigo-500/25 group transition-all duration-300 h-full"
      >
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 py-1 px-4 rounded-full bg-[#f1c40f] text-slate-900 text-[10px] font-black uppercase tracking-widest shadow-[0_4px_12px_rgba(241,196,15,0.3)] border border-white/20 whitespace-nowrap">
          {t.landing.pricing.beneficial}
        </div>
        <div className="flex-1 flex flex-col items-center">
          <span className="text-[10px] md:text-[11px] font-bold text-indigo-100 uppercase tracking-widest mb-1 mt-1">
            {t.userSettings.trialExpired.yearly}
          </span>
          <div className="flex items-baseline gap-1 mb-3 md:mb-4">
            <span className="text-2xl md:text-3xl font-black">
              {t.landing.pricing.oneYearPrice}
            </span>
            <span className="text-indigo-100 text-xs md:text-sm font-medium">
              {t.userSettings.trialExpired.periodYear}
            </span>
          </div>
        </div>
        <button
          onClick={() => onSubscribe("yearly")}
          disabled={isLoading}
          className="w-full mt-auto flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white text-indigo-700 font-black text-sm md:text-base shadow-xl hover:bg-indigo-50 transition-all duration-300 transform active:scale-95 disabled:opacity-50"
        >
          <span>{t.userSettings.trialExpired.subscribe}</span>
          <Zap size={14} className="fill-current" />
        </button>
      </motion.div>

      {/* Lifetime - commented out as requested
      <motion.div
        whileHover={{ y: -3 }}
        className="relative p-5 md:p-6 rounded-[2rem] bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 flex flex-col items-center text-center group transition-colors hover:border-rose-200 dark:hover:border-rose-900 shadow-sm h-full"
      >
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 py-1 px-3 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[9px] font-black uppercase tracking-wider shadow-sm border border-rose-100 dark:border-rose-800/50 whitespace-nowrap">
          {t.landing.pricing.lifetime}
        </div>
        <div className="flex-1 flex flex-col items-center">
          <span className="text-[10px] md:text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1 mt-1 opacity-0">
            {t.userSettings.trialExpired.monthly}
          </span>
          <div className="flex items-baseline gap-1 mb-3 md:mb-4">
            <span className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white">
              {t.landing.pricing.lifetimePrice}
            </span>
            <span className="text-slate-500 dark:text-slate-400 text-xs md:text-sm font-medium opacity-0">
              {t.userSettings.trialExpired.periodMonth}
            </span>
          </div>
        </div>
        <button
          onClick={() => onSubscribe("lifetime")}
          disabled={isLoading}
          className="w-full mt-auto flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-br from-rose-600 to-rose-700 text-white font-black text-sm md:text-base shadow-lg hover:from-rose-500 hover:to-rose-600 transition-all duration-300 transform active:scale-95 disabled:opacity-50"
        >
          <span>{t.userSettings.trialExpired.buyNow}</span>
          <Zap size={14} className="fill-current" />
        </button>
      </motion.div>
      */}
    </div>
  );
}
